import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, X, ArrowRight, Check, Loader2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { GdprConsent } from '../GdprConsent';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/newsletter/subscribe`;

const texts = {
  ca: {
    title: 'Inscriu-te a la nostra Newsletter',
    placeholder: 'e-mail',
    accept: '* Accepto la',
    privacy: 'política de privacitat',
    success: 'Gràcies! T\'has subscrit correctament.',
    error: 'Hi ha hagut un error. Torna-ho a intentar.',
    needAccept: 'Cal acceptar la política de privacitat.'
  },
  es: {
    title: 'Inscríbete a nuestra Newsletter',
    placeholder: 'e-mail',
    accept: '* Acepto la',
    privacy: 'política de privacidad',
    success: '¡Gracias! Te has suscrito correctamente.',
    error: 'Ha habido un error. Inténtalo de nuevo.',
    needAccept: 'Debes aceptar la política de privacidad.'
  },
  fr: {
    title: 'Inscrivez-vous à notre Newsletter',
    placeholder: 'e-mail',
    accept: '* J\'accepte la',
    privacy: 'politique de confidentialité',
    success: 'Merci ! Votre inscription a bien été enregistrée.',
    error: 'Une erreur est survenue. Veuillez réessayer.',
    needAccept: 'Vous devez accepter la politique de confidentialité.'
  },
  en: {
    title: 'Subscribe to our Newsletter',
    placeholder: 'e-mail',
    accept: '* I accept the',
    privacy: 'privacy policy',
    success: 'Thank you! You have been subscribed.',
    error: 'Something went wrong. Please try again.',
    needAccept: 'You must accept the privacy policy.'
  }
};

export const NewsletterModal = ({ isOpen, onClose }) => {
  const { language } = useLanguage();
  const t = texts[language] || texts.ca;

  const [email, setEmail] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error | needAccept

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      setStatus('needAccept');
      return;
    }
    setStatus('loading');
    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, language })
      });
      if (!res.ok) throw new Error('subscribe failed');
      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus('error');
    }
  };

  const handleClose = () => {
    setStatus('idle');
    setAccepted(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={handleClose}
          data-testid="newsletter-modal"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3 }}
            className="relative w-full max-w-md bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-3 right-3 p-2 hover:bg-[#F0F2EF] rounded-full transition-colors duration-200"
              aria-label="Close"
              data-testid="newsletter-close-btn"
            >
              <X className="w-5 h-5 text-[#666666]" />
            </button>

            <div className="px-8 py-10">
              <h3 className="text-xl font-bold text-[#1A1A1A] text-center mb-8">
                {t.title}
              </h3>

              {status === 'success' ? (
                <div className="flex flex-col items-center gap-4 py-4" data-testid="newsletter-success">
                  <div className="w-12 h-12 rounded-full bg-[#1A4D2E] flex items-center justify-center">
                    <Check className="w-6 h-6 text-white" />
                  </div>
                  <p className="text-sm text-[#1A4D2E] font-medium text-center">{t.success}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex items-center justify-center w-12 h-12 border border-[#999999] shrink-0">
                      <Mail className="w-6 h-6 text-[#666666]" strokeWidth={1.5} />
                    </div>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t.placeholder}
                      className="flex-1 min-w-0 border-b border-[#999999] pb-2 text-[#1A1A1A] placeholder-[#AAAAAA] tracking-wide focus:outline-none focus:border-[#1A4D2E] bg-transparent"
                      data-testid="newsletter-email-input"
                    />
                    <button
                      type="submit"
                      disabled={status === 'loading'}
                      className="flex items-center justify-center w-10 h-10 border border-[#999999] text-[#666666] hover:bg-[#1A4D2E] hover:border-[#1A4D2E] hover:text-white transition-colors duration-200 shrink-0 disabled:opacity-50"
                      aria-label="Subscribe"
                      data-testid="newsletter-submit-btn"
                    >
                      {status === 'loading' ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ArrowRight className="w-4 h-4" />
                      )}
                    </button>
                  </div>

                  <GdprConsent
                    privacy={accepted}
                    onPrivacyChange={(checked) => { setAccepted(checked); if (status === 'needAccept') setStatus('idle'); }}
                    idPrefix="newsletter"
                  />

                  {status === 'needAccept' && (
                    <p className="mt-3 text-xs text-red-600" data-testid="newsletter-need-accept-msg">{t.needAccept}</p>
                  )}
                  {status === 'error' && (
                    <p className="mt-3 text-xs text-red-600" data-testid="newsletter-error-msg">{t.error}</p>
                  )}
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default NewsletterModal;
